import React, { useState } from "react";
import { Link } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import { useGetUsersQuery, useDeleteUserMutation } from "../../store/api";

const UserList = () => {
  const { data, isLoading, isError, error } = useGetUsersQuery();
  const [deleteUser] = useDeleteUserMutation();

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
    userName: "",
  });

  const users = Array.isArray(data) ? data : [];

  const handleDelete = (userId, userName) => {
    setDeleteModal({ isOpen: true, userId, userName });
  };

  const confirmDelete = async () => {
    try {
      await deleteUser(deleteModal.userId).unwrap();
      setDeleteModal({ isOpen: false, userId: null, userName: "" });
    } catch (err) {
      // Error handled by UI
    }
  };

  const columns = [
    { key: "displayName", label: "Display Name" },
    { key: "accessLevel", label: "Access Level" },
    { key: "status", label: "Status" },
    { key: "lastLogin", label: "Last Login" },
    { key: "actions", label: "Actions" },
  ];

  const formatLastLogin = (lastLoginAt) => {
    if (!lastLoginAt) return "Never";
    const date = new Date(lastLoginAt);
    return date.toLocaleString();
  };

  const rows = users.map((user) => {
    const id = user.userId || user.id;
    return {
      id,
      displayName: user.displayName || "—",
      accessLevel: user.accessLevelName || "—",
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {user.isActive ? "Active" : "Inactive"}
        </span>
      ),
      lastLogin: formatLastLogin(user.lastLoginAt),
      actions: (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/users/${id}/edit`}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-indigo-600 transition hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
          >
            Edit
          </Link>
          <button
            type="button"
            onClick={() => handleDelete(id, user.displayName)}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/30"
          >
            Delete
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle="Manage user accounts and access."
        actions={
          <Link to="/users/new">
            <Button size="sm">Add User</Button>
          </Link>
        }
      />

      <Card className="p-0">
        <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
          All Users
        </div>
        <div className="px-6 py-4">
          {isLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Loading users...
            </p>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load users."}
            </Alert>
          )}
          {!isLoading && !isError && (
            <Table columns={columns} rows={rows} emptyText="No users yet." />
          )}
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, userId: null, userName: "" })
        }
        title="Delete User"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.userName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default UserList;
